import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { MagnifyingGlass, ArrowSquareOut, Flask } from "@phosphor-icons/react";

const fmtMoney = (n) => {
  if (n == null) return "—";
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n)}`;
};

function SourceBadge({ source }) {
  const color = source === "NIH" ? "bg-emerald-700" : "bg-blue-700";
  return (
    <span className={`${color} text-white text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 shrink-0`}>
      {source}
    </span>
  );
}

export default function ResearchDrawer({ open, onOpenChange, defaultKeyword }) {
  const [keyword, setKeyword] = useState(defaultKeyword || "");
  const [projects, setProjects] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  const run = async (kw) => {
    const q = (kw || "").trim();
    if (q.length < 3) return;
    setLoading(true);
    setError(null);
    setSearched(true);
    const [projRes, statsRes] = await Promise.allSettled([
      api.get("/research/funded-projects", { params: { keyword: q } }),
      api.get("/research/award-stats", { params: { keyword: q } }),
    ]);
    if (projRes.status === "fulfilled") setProjects(projRes.value.data);
    else setProjects(null);
    if (statsRes.status === "fulfilled") setStats(statsRes.value.data);
    else setStats(null);
    if (projRes.status === "rejected" && statsRes.status === "rejected") {
      setError("Research sources are temporarily unavailable. Try again shortly.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open && !searched && defaultKeyword) {
      setKeyword(defaultKeyword);
      run(defaultKeyword);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="research-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 font-display tracking-tight">
            <Flask size={18} className="text-blue-700" /> Funding research
          </SheetTitle>
          <SheetDescription className="text-xs">
            Past funded projects from NIH &amp; NSF, and typical award sizes from USAspending.gov.
          </SheetDescription>
        </SheetHeader>

        <div className="flex gap-2 mt-4">
          <Input
            data-testid="research-keyword-input"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run(keyword)}
            placeholder="Topic keyword (e.g. rural broadband)"
            className="gf-sharp border-zinc-300"
          />
          <Button
            onClick={() => run(keyword)}
            disabled={loading || keyword.trim().length < 3}
            data-testid="research-search-btn"
            className="gf-sharp bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide"
          >
            <MagnifyingGlass size={15} />
          </Button>
        </div>

        {loading && (
          <div className="mt-6 text-xs font-mono uppercase tracking-widest text-zinc-500 animate-pulse" data-testid="research-loading">
            Querying NIH · NSF · USAspending…
          </div>
        )}
        {error && !loading && (
          <div className="mt-6 border border-red-200 bg-red-50 text-red-800 text-sm px-4 py-3" data-testid="research-error">{error}</div>
        )}

        {!loading && stats && (
          <div className="mt-6" data-testid="research-stats">
            <div className="gf-overline">Typical award sizes · USAspending</div>
            <div className="grid grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 mt-2">
              {[
                { label: "Grants (6yr)", value: (stats.grant_count ?? 0).toLocaleString() },
                { label: "Median", value: fmtMoney(stats.stats?.median) },
                { label: "Average", value: fmtMoney(stats.stats?.avg) },
                { label: "Largest", value: fmtMoney(stats.stats?.max) },
              ].map((s) => (
                <div key={s.label} className="bg-white p-3">
                  <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">{s.label}</div>
                  <div className="font-display font-black text-lg tracking-tight mt-0.5">{s.value}</div>
                </div>
              ))}
            </div>
            {stats.stats && (
              <div className="text-[10px] text-zinc-400 font-mono mt-1">
                Median/avg/largest from the top {stats.stats.sample_size} matching grants.
              </div>
            )}
            {(stats.top_awards || []).length > 0 && (
              <div className="mt-3 border border-zinc-200 divide-y divide-zinc-100">
                {stats.top_awards.map((a, i) => (
                  <a
                    key={i}
                    href={a.url || "#"}
                    target="_blank"
                    rel="noreferrer"
                    className="block px-3 py-2 hover:bg-zinc-50"
                    data-testid={`research-top-award-${i}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-zinc-900 truncate">{a.recipient || "Unknown recipient"}</span>
                      <span className="text-xs font-mono font-bold text-emerald-700 shrink-0">{fmtMoney(a.amount)}</span>
                    </div>
                    <div className="text-[10px] font-mono text-zinc-500 truncate">{a.agency}</div>
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {!loading && projects && (
          <div className="mt-6" data-testid="research-projects">
            <div className="gf-overline">Similar funded projects · NIH + NSF</div>
            {(projects.unavailable_sources || []).length > 0 && (
              <div className="text-[10px] text-amber-700 font-mono mt-1">
                {projects.unavailable_sources.join(" & ")} temporarily unavailable.
              </div>
            )}
            {projects.projects.length === 0 ? (
              <div className="text-sm text-zinc-500 mt-2">No funded projects found for this keyword. Try a broader term.</div>
            ) : (
              <div className="mt-2 border border-zinc-200 divide-y divide-zinc-100">
                {projects.projects.map((pr, i) => (
                  <a
                    key={`${pr.source}-${pr.id}-${i}`}
                    href={pr.url}
                    target="_blank"
                    rel="noreferrer"
                    className="block px-3 py-2.5 hover:bg-zinc-50 group"
                    data-testid={`research-project-${i}`}
                  >
                    <div className="flex items-start gap-2">
                      <SourceBadge source={pr.source} />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold text-zinc-900 leading-snug group-hover:text-blue-700">
                          {pr.title}
                          <ArrowSquareOut size={11} className="inline ml-1 text-zinc-400" />
                        </div>
                        <div className="text-[10px] font-mono text-zinc-500 mt-1 flex flex-wrap gap-x-3">
                          {pr.pi && <span>PI: {pr.pi}</span>}
                          {pr.organization && <span className="truncate max-w-[180px]">{pr.organization}</span>}
                          {pr.amount != null && <span className="text-emerald-700 font-bold">{fmtMoney(pr.amount)}</span>}
                          {pr.start_date && <span>{pr.start_date}</span>}
                        </div>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
